// CF-REMOVE-REDSCREEN-WALLS-V1 (PL #7138; Scorer design-alignment 2026-05-29 1340Z).
//
// The two red full-screen walls — App's "Service temporarily unavailable"
// backend-down wall and CoinFlip's "Service data unavailable" balance-error wall —
// are removed. Removing them must NOT open a stale-balance hole: a custodial
// balance is safe to DISPLAY across transient transport blips, but real-money
// actions (flip / withdraw) must stay fail-closed on a CURRENT, fully-parsed
// balance. This module is the pure, framework-free core of that policy — two
// decisions that are unit-tested directly (the App.tsx wiring around them is
// static-pattern tested):
//
//   1. shouldRetainOnFailure — which /api/v1/balance fetch failures may keep the
//      last loaded balance on screen (transport class) vs which must clear it to a
//      neutral, non-actionable state (schema / auth / DTO class).
//   2. isBalanceFresh — whether a loaded snapshot is recent enough to ACT on.
//      Display may show an older balance; flip/withdraw may not act on a stale one.

// Bounded action-freshness budget. 45s = three consecutive 15s balance-poll
// intervals (Scorer's recommended primary value; 60s was the looser alternative).
// MUST be finite — an unbounded "loaded ⇒ actionable" is precisely the silent
// stale fallback the wall removal must not introduce.
export const BALANCE_FRESHNESS_BUDGET_MS = 45_000;

/**
 * A loaded balance snapshot is fresh enough for a real-money action only when it
 * was loaded within the freshness budget.
 *
 * - `null` / `undefined` (never loaded) ⇒ never fresh.
 * - A future `loadedAtMs` (benign clock skew) ⇒ fresh, never spuriously stale.
 */
export function isBalanceFresh(loadedAtMs: number | null | undefined, nowMs: number): boolean {
    if (loadedAtMs == null) return false;
    return nowMs - loadedAtMs <= BALANCE_FRESHNESS_BUDGET_MS;
}

/**
 * Classification of a /api/v1/balance fetch failure for the retain-vs-clear
 * decision.
 * - `network`     — fetch threw / aborted on timeout (no HTTP response).
 * - `http`        — a non-ok HTTP status was returned.
 * - `json_parse`  — the body was not valid JSON.
 * - `invalid_dto` — JSON parsed but failed atomic shape / tier_config validation.
 */
export type BalanceFetchFailure =
    | { kind: 'network' }
    | { kind: 'http'; status: number }
    | { kind: 'json_parse' }
    | { kind: 'invalid_dto'; code: string };

/**
 * Whether a fetch failure may RETAIN the last successfully-loaded balance on
 * screen (transport class) or MUST clear it to a neutral non-actionable state
 * (schema / auth / DTO class — never bless stale data as normal).
 *
 * Transport class (retain): network/timeout, and the transient gateway statuses
 * 502 / 503 / 504. Everything else clears — all other 4xx (auth/init-data 401/403,
 * 400, 404), any 5xx outside the gateway set (e.g. 500), malformed JSON, and any
 * invalid DTO / tier_config. The allowlist is explicit; an unrecognised failure
 * clears rather than retains (fail-closed default).
 */
export function shouldRetainOnFailure(failure: BalanceFetchFailure): boolean {
    switch (failure.kind) {
        case 'network':
            return true;
        case 'http':
            return failure.status === 502 || failure.status === 503 || failure.status === 504;
        case 'json_parse':
        case 'invalid_dto':
            return false;
    }
}
